import { useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import PageHeader from "../../components/PageHeader"
import _ from "lodash"

import { useQuery } from "@apollo/client"
import { SplitButton } from "primereact/splitbutton"
// queries
import { ENGAGEMENTS_PARTICIPANT_GROUP_BY_TEMPLATE } from "./queries"

// Context
import { UserContext } from "../../context/userContext"

import LoaderFullPage from "../../components/Loaders"

import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import ParticipantProfileBoxSmall from "../../components/ParticipantProfileBoxSmall"
import CustomTemplateName from "../../components/CustomTemplateName"

import GLOBALS_STATUS from "../../config/globalsEngagementsStatus"
import { Button } from "primereact/button"
import GenericTable from "../../components/DataTable/GenericTable"
import FilterHeader from "../../components/DataTable/FilterHeader"
import { useFilters } from "../../components/Filters/useFilters"
import { Calendar } from "primereact/calendar"
import useLabels from "../../hooks/useLabels"

import { parseDate } from "../../helpers/dateNormalization"
import { formatDate } from "../../components/DataTable/helpers"
import { handleOptsForFilter } from "../../components/DataTable/helpers"
import { navigateWithOrigin } from "../../helpers/navigation"

export default function PlanSelfCare() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const user = useContext(UserContext)
  const [dateRange, setDateRange] = useState(null)

  const currentYear = new Date().getFullYear()
  const yearRange = `2000:${currentYear + 10}`
  const navigate = useNavigate()

  const ID_TEMPLATES = ["plan_self_care_engagement"]
  const ID_BLOCKS = ["session", "session__posc_plan"]
  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
  } = useFilters()
  const [loadingLabels, labels] = useLabels(["care_coordination_session"])

  useQuery(ENGAGEMENTS_PARTICIPANT_GROUP_BY_TEMPLATE, {
    variables: {
      participant_id: user.participant_id,
      organization_id: user.organization_id,
      idTemplates: ID_TEMPLATES,
      idBlocks: ID_BLOCKS,
    },
    fetchPolicy: "no-cache",
    skip: !user.staff_id,
    onCompleted: async (data) => {
      if (data.engagementsParticipant === null) {
        setData(null)
        return setLoading(false)
      }
      const dataFormatted = JSON.parse(data.engagementsParticipant.data)
      const formattedData = _.map(dataFormatted, (template) => {
        const blocks = _.groupBy(template, "block_name")
        const session = _.isEmpty(blocks?.session) ? {} : blocks?.session[0]
        const handleLocations = () => {
          const locationsList = _.map(
            session.values?.session__location_ids,
            ({ label }) => label
          )
          return _.join(locationsList, ", ")
        }
        let actionButton
        _.includes(
          GLOBALS_STATUS.ACTIVE_STATUS,
          user.participant_membership_status
        )
          ? (actionButton = (
              <SplitButton
                label="View"
                to={`/form/${session.idTemplate}/1/session.values.idStamp}`}
                onClick={() => {
                  user.changeParticipant(session.participant_id)
                  navigateWithOrigin(
                    navigate,
                    `/form/view/${session.idTemplate}/${session.idStamp}`
                  )
                }}
                model={[
                  {
                    label: "Edit",
                    command: () => {
                      navigate(
                        `/form/${session.idTemplate}/1/${session.idStamp}`
                      )
                    },
                  },
                ]}
              />
            ))
          : (actionButton = (
              <Button
                label="View"
                to={`/form/${session.idTemplate}/1/session.values.idStamp}`}
                onClick={() => {
                  user.changeParticipant(session.participant_id)
                  navigateWithOrigin(
                    navigate,
                    `/form/view/${session.idTemplate}/${session.idStamp}`
                  )
                }}
              />
            ))

        return {
          date: parseDate(session?.values.session_date),
          locations: handleLocations(),
          facilitating_staff: session.staff_full_name,
          channel: session.values.session_channel,
          length: `${session.values.session_length} minutes`,

          edit: actionButton,
        }
      })
      setData(formattedData)
      setLoading(false)
    },
  })

  if (loading || loadingLabels) return <LoaderFullPage />
  const uniqueLocations = handleOptsForFilter(data, "locations")
  const uniqueFacilitatingStaff = handleOptsForFilter(
    data,
    "facilitating_staff"
  )

  const filterConfig = [
    {
      label: "Date",
      value: dateRange,
      options: null,
      onChange: (e) => {
        setDateRange(e.value)
        onFilterChange("date", e.value)
      },
      onClear: () => {
        setDateRange(null)
        onFilterChange("date", null)
      },
      enableFilter: true,
      component: (
        <Calendar
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.value)
            onFilterChange("date", e.value)
          }}
          placeholder="Select Date Range"
          selectionMode="range"
          dateFormat="mm/dd/yy"
          readOnlyInput
          numberOfMonths={2}
          showIcon
          showButtonBar
          yearNavigator
          yearRange={yearRange}
        />
      ),
    },
    {
      label: "Location",
      value: filters.locations?.value || "",
      options: uniqueLocations,
      onChange: (e) => onFilterChange("locations", e.value),
      onClear: () => onFilterChange("locations", null),
      enableFilter: true,
    },
    {
      label: "Facilitating Staff",
      value: filters.facilitating_staff?.value || "",
      options: uniqueFacilitatingStaff,
      onChange: (e) => onFilterChange("facilitating_staff", e.value),
      onClear: () => onFilterChange("facilitating_staff", null),
      enableFilter: true,
    },
  ]

  const columns = [
    {
      field: "date",
      header: "Date",
      sortable: true,
      body: (rowData) => formatDate(rowData.date),
    },
    { field: "locations", header: "Location", sortable: true },
    { field: "channel", header: "Engagement Channel", sortable: true },
    {
      field: "facilitating_staff",
      header: "Facilitating Staff",
      sortable: true,
    },
    { field: "length", header: "Session Length", sortable: true },
    { field: "edit", header: "Action" },
  ]

  const handleClearAllFilters = () => {
    clearAllFilters()
    setDateRange(null)
  }

  return (
    <div className="layout-with-sidebar">
      <div className="sidebar">
        <ParticipantSidebar />
      </div>
      <div className="main-content">
        <ParticipantProfileBoxSmall />
        <div>
          <div>
            <PageHeader
              title={
                <CustomTemplateName
                  parent="plan_self_care_engagement"
                  original_label="Plan of Self Care Engagement"
                />
              }
              actions={
                _.includes(
                  GLOBALS_STATUS.ACTIVE_STATUS,
                  user.participant_membership_status
                )
                  ? [
                      {
                        label: (
                          <>
                            New{" "}
                            <CustomTemplateName
                              parent="plan_self_care_engagement"
                              original_label="Plan of Self Care Engagement"
                            />
                          </>
                        ),
                        to: "/form/plan_self_care_engagement/1",
                      },
                    ]
                  : []
              }
            />
            <GenericTable
              data={data}
              columns={columns}
              filters={filters}
              header={
                <FilterHeader
                  filters={filterConfig}
                  globalFilterValue={globalFilterValue}
                  onGlobalFilterChange={onGlobalFilterChange}
                  clearGlobalFilter={clearGlobalFilter}
                  clearAllFilters={handleClearAllFilters}
                />
              }
              paginator
              rows={50}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
